import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import complianceService from "services/compliance";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";

const DEFAULT_CONFIG = {
  kSteps: 1,
  kycExpiryThresholdDays: 30,
  sanctionsEscalationHours: 24,
  fpThreshold: 0.2,
};

const isDev = process.env.NODE_ENV === "development";
const logDebug = (...args) => {
  if (isDev && typeof console !== "undefined" && console.debug) {
    // eslint-disable-next-line no-console
    console.debug("[ComplianceTaskSettings]", ...args);
  }
};

const ComplianceTaskSettings = ({ orgAdminId }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);

  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [recomputeMessage, setRecomputeMessage] = useState(null);

  const orgAdminHint = useMemo(() => orgAdminId || "—", [orgAdminId]);

  const loadConfig = useCallback(async () => {
    setIsLoadingConfig(true);
    setConfigError(null);

    try {
      const { data } = await complianceService.getComplianceTaskSettings();
      if (data && typeof data === "object") {
        setConfig((previous) => ({ ...previous, ...data }));
      }
      logDebug("Task settings loaded", data);
    } catch (error) {
      setConfigError(
        error?.message || "Unable to load the compliance task settings."
      );
      logDebug("Task settings load failed", error);
    } finally {
      setIsLoadingConfig(false);
    }
  }, []);

  const loadSnapshot = useCallback(async () => {
    setIsLoadingSnapshot(true);
    setSnapshotError(null);
    try {
      const { data } = await complianceService.fetchComplianceSnapshot();
      setSnapshot(data);
      logDebug("Snapshot loaded", data?.generatedAt);
    } catch (error) {
      setSnapshot(null);
      setSnapshotError(
        error?.message ||
          "Unable to fetch the latest compliance snapshot right now."
      );
      logDebug("Snapshot load failed", error);
    } finally {
      setIsLoadingSnapshot(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadSnapshot();
  }, [loadConfig, loadSnapshot]);

  const handleConfigChange = (key, value) => {
    setConfig((previous) => ({ ...previous, [key]: value }));
  };

  const handleSaveConfig = async () => {
    setIsSavingConfig(true);
    setConfigError(null);

    try {
      await complianceService.updateComplianceTaskSettings(config);
      await loadConfig();
      logDebug("Task settings updated");
    } catch (error) {
      setConfigError(
        error?.message || "Unable to save the compliance task settings."
      );
      logDebug("Task settings update failed", error);
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleRecompute = async () => {
    setIsRecomputing(true);
    setRecomputeMessage(null);
    try {
      const { data } = await complianceService.recomputeComplianceAnalysis();
      setRecomputeMessage(
        `Recomputed ${formatNairobiDate(data?.generatedAt)} • ${
          data?.count || 0
        } entities rescored`
      );
      await loadSnapshot();
      logDebug("Compliance recompute completed", data);
    } catch (error) {
      setRecomputeMessage(
        error?.message || "Unable to recompute the task queue right now."
      );
      logDebug("Compliance recompute failed", error);
    } finally {
      setIsRecomputing(false);
    }
  };

  const hasSnapshotUrls =
    snapshot?.urls &&
    (snapshot.urls.riskRegisterUrl ||
      snapshot.urls.snapshotJsonUrl ||
      snapshot.urls.snapshotCsvUrl);

  return (
    <section className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <header className="space-y-1">
        <h3 className="text-xl font-semibold text-gray-900">
          Compliance Task Settings
        </h3>
        <p className="text-sm text-gray-600">
          Configure the compliance task queue parameters for your organization.
        </p>
        <p className="text-xs text-gray-500">
          OrgAdmin ID:{" "}
          <span className="font-medium text-gray-700">{orgAdminHint}</span>
        </p>
      </header>

      <div className="space-y-3 rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleRecompute}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isRecomputing}
          >
            {isRecomputing ? "Recomputing…" : "Recompute analysis"}
          </button>
          <button
            type="button"
            onClick={loadSnapshot}
            className="rounded-md border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isLoadingSnapshot}
          >
            {isLoadingSnapshot ? "Refreshing…" : "Refresh snapshot"}
          </button>
        </div>
        {recomputeMessage && (
          <p className="text-sm text-indigo-800">{recomputeMessage}</p>
        )}
        {snapshotError && (
          <p className="text-sm text-red-600">{snapshotError}</p>
        )}
        {hasSnapshotUrls && (
          <div className="space-y-2 text-sm text-indigo-900">
            <p className="font-semibold">
              Latest snapshot · {formatNairobiDate(snapshot?.generatedAt)}
            </p>
            <div className="flex flex-wrap gap-3">
              {snapshot?.urls?.riskRegisterUrl && (
                <a
                  href={snapshot.urls.riskRegisterUrl}
                  className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100"
                >
                  Risk register
                </a>
              )}
              {snapshot?.urls?.snapshotJsonUrl && (
                <a
                  href={snapshot.urls.snapshotJsonUrl}
                  className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100"
                >
                  Snapshot JSON
                </a>
              )}
              {snapshot?.urls?.snapshotCsvUrl && (
                <a
                  href={snapshot.urls.snapshotCsvUrl}
                  className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100"
                >
                  Snapshot CSV
                </a>
              )}
            </div>
          </div>
        )}
        {!hasSnapshotUrls && !snapshotError && !isLoadingSnapshot && (
          <p className="text-sm text-indigo-900">
            Snapshot artifacts will appear after the first recompute completes.
          </p>
        )}
      </div>

      <div className="space-y-4">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Model configuration
        </h4>
        {isLoadingConfig ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <SpinnerIcon className="h-4 w-4 animate-spin text-sflPrimary" />
            Loading configuration…
          </div>
        ) : (
          <form
            className="grid gap-4 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              handleSaveConfig();
            }}
          >
            <NumberInput
              label="Markov steps (k)"
              value={config.kSteps}
              min={1}
              onChange={(value) => handleConfigChange("kSteps", value)}
            />
            <NumberInput
              label="KYC expiry threshold (days)"
              value={config.kycExpiryThresholdDays}
              min={1}
              onChange={(value) =>
                handleConfigChange("kycExpiryThresholdDays", value)
              }
            />
            <NumberInput
              label="Sanctions escalation window (hours)"
              value={config.sanctionsEscalationHours}
              min={1}
              onChange={(value) =>
                handleConfigChange("sanctionsEscalationHours", value)
              }
            />
            <NumberInput
              label="False-positive threshold"
              value={config.fpThreshold}
              min={0}
              step={0.05}
              onChange={(value) => handleConfigChange("fpThreshold", value)}
            />
            <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                className="rounded-md bg-sflPrimary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSavingConfig}
              >
                {isSavingConfig ? "Saving…" : "Save settings"}
              </button>
              <button
                type="button"
                onClick={loadConfig}
                className="rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
                disabled={isSavingConfig}
              >
                Reload
              </button>
            </div>
            {configError && (
              <p className="md:col-span-2 text-sm text-red-600">
                {configError}
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
};

ComplianceTaskSettings.propTypes = {
  orgAdminId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ComplianceTaskSettings.defaultProps = {
  orgAdminId: null,
};

const NumberInput = ({ label, value, min, step, onChange }) => (
  <label className="flex flex-col gap-1 text-sm text-gray-700">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <input
      type="number"
      value={value ?? ""}
      min={min}
      step={step}
      onChange={(event) => onChange(Number(event.target.value))}
      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
    />
  </label>
);

NumberInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  min: PropTypes.number,
  step: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

NumberInput.defaultProps = {
  value: "",
  min: undefined,
  step: 1,
};

const formatNairobiDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString("en-GB", {
    timeZone: "Africa/Nairobi",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default ComplianceTaskSettings;
